import { useCallback, useEffect, useRef, useState } from 'react';

import { fulfillmentLifecycleApi } from 'api/fulfillmentLifecycle';

/**
 * Custom hook for fetching route breakdown data (pallets + status breakdown).
 *
 * Calls the /status/{lineHaulId}/breakdown endpoint when a route is provided.
 * Aborts in-flight requests on unmount or when the route changes.
 *
 * @param {Object} params
 * @param {Object|null} params.route - Route object from the /status API response
 * @param {number|null} params.facilityId - Facility ID to query
 * @param {string} params.selectedDate - Operating date in YYYY-MM-DD format
 * @returns {{ breakdownData: Object|null, loading: boolean, error: string|null }}
 */
const useRouteBreakdown = ({ facilityId, route, selectedDate }) => {
    const [breakdownData, setBreakdownData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const abortControllerRef = useRef(null);

    const fetchBreakdown = useCallback(async () => {
        if (!route || !facilityId || !selectedDate) {
            setBreakdownData(null);
            setError(null);
            setLoading(false);
            return;
        }

        // Cancel previous request if still in flight
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        setLoading(true);
        setError(null);

        try {
            const response = await fulfillmentLifecycleApi.getBreakdown({
                facilityId,
                lineHaulId: route.lineHaulId,
                packDate: selectedDate,
                signal: abortController.signal,
            });

            if (abortController.signal.aborted) {
                return;
            }

            if (response.status) {
                setBreakdownData(response.data || null);
            } else {
                setBreakdownData(null);
                setError(response.msg || 'Failed to fetch route breakdown');
            }
        } catch (err) {
            if (err.name === 'AbortError') {
                return;
            }
            setBreakdownData(null);
            setError(err.message || 'An unexpected error occurred');
        } finally {
            if (!abortController.signal.aborted) {
                setLoading(false);
            }
        }
    }, [route, facilityId, selectedDate]);

    useEffect(() => {
        fetchBreakdown();

        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [fetchBreakdown]);

    return { breakdownData, loading, error };
};

export { useRouteBreakdown };
